import React, { useState } from 'react';
import { useQuery } from 'react-query';

import { Typography } from 'antd';
import styled from 'styled-components';

import { getCommunityContacts } from 'apis/CommunityAPI';
import ManageContactsModal from 'components/Communities/contacts/ManageContactsModal';
import { useRootContext } from 'components/layout/RootContext';
import Button from 'components/lib/Button';
import { CommunityContactType } from 'types/communities';

import { useEditShopRequestModalContext } from './EditShopRequestModalContext';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 22px;
  max-width: 500px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 4px;
  border-bottom: 1px solid var(--line-gray);
`;

const Title = styled(Typography.Text)`
  font-family: var(--font-bold);
  font-size: 14px;
  color: var(--text-primary);
`;

const EditButton = styled(Button)`
  color: var(--dark-blue);
  cursor: pointer;
  font-size: 12px;
  padding: 0;
  box-shadow: none;
`;

const CommunityContactsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 140px;
  overflow-y: auto;
  padding-right: 6px;
`;

const CommunityContactItem = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
`;

const CommunityContactDetails = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const CommunityContactName = styled(Typography.Text)`
  font-size: 13px;
  font-family: var(--font-bold);
  line-height: 1.35;
  color: var(--text-primary);
  word-break: break-word;
  min-width: 0;
`;

const CommunityContactValue = styled(Typography.Text)`
  font-size: 13px;
  line-height: 1.35;
  color: var(--text-secondary);
  word-break: break-word;
  min-width: 0;
`;

const EmptyState = styled(Typography.Text)`
  font-size: 13px;
  color: var(--text-secondary);
`;

const EditShopRequestModalCommunityContacts = () => {
  const { showMessage } = useRootContext();
  const { shopRequest } = useEditShopRequestModalContext();

  const [manageContactsOpened, setManageContactsOpened] = useState(false);
  const community = shopRequest?.community;
  const communityId = community?.id;

  const {
    data: communityContacts = [],
    isFetching,
    isLoading,
  } = useQuery<CommunityContactType[]>({
    queryKey: ['community', 'contacts', communityId],
    queryFn: () => getCommunityContacts(communityId as number),
    enabled: Boolean(communityId),
    refetchOnWindowFocus: false,
    onError: () => {
      showMessage('error', 'Failed to fetch community contacts.');
    },
  });
  const shopRequestContacts = communityContacts.filter(
    (communityContact) => communityContact.shop_request_contact_enabled
  );

  return (
    <>
      <Container>
        <Header>
          <Title>Community Contacts</Title>
          <EditButton size="small" type="link" onClick={() => setManageContactsOpened(true)} disabled={!community}>
            Edit
          </EditButton>
        </Header>

        {(isLoading || isFetching) && <EmptyState>Loading community contacts...</EmptyState>}

        {!isLoading && !isFetching && shopRequestContacts.length === 0 && (
          <EmptyState>No community contacts are enabled for shop requests.</EmptyState>
        )}

        {!isLoading && !isFetching && shopRequestContacts.length > 0 && (
          <CommunityContactsList className="mini-scroll">
            {shopRequestContacts.map((communityContact) => (
              <CommunityContactItem key={communityContact.id}>
                <CommunityContactDetails>
                  <CommunityContactName>{communityContact.name}</CommunityContactName>
                  <CommunityContactValue>{communityContact.email}</CommunityContactValue>
                  {communityContact.phone && <CommunityContactValue>{communityContact.phone}</CommunityContactValue>}
                </CommunityContactDetails>
              </CommunityContactItem>
            ))}
          </CommunityContactsList>
        )}
      </Container>
      {manageContactsOpened && community && (
        <ManageContactsModal
          openModal={manageContactsOpened}
          communityInfo={community}
          communityContacts={communityContacts}
          isFetchingCommunityContacts={isFetching}
          isLoadingCommunityContacts={isLoading}
          onClose={() => setManageContactsOpened(false)}
        />
      )}
    </>
  );
};

export default EditShopRequestModalCommunityContacts;
